export const SALUTE_PAGES_EN = [
  {
    path: '/cassa-malati',
    kind: 'area',
    area: 'Health',
    areaPath: '/cassa-malati',
    eyebrow: 'Health area',
    title: 'Health insurance in Switzerland: basic cover, supplementary plans and income protection.',
    summary: 'Compare Swiss basic health insurance, supplementary cover, deductibles, accident protection and income protection around your real situation.',
    seoTitle: 'Health insurance in Switzerland | Broker advice',
    seoDescription: 'Swiss health insurance advice covering basic LAMal/KVG, supplementary insurance, deductibles, accident cover and income protection. Review your current cover.',
    keywords: ['health insurance switzerland', 'LAMal KVG', 'supplementary insurance switzerland', 'insurance broker switzerland'],
    image: '/assets/soluzione-salute.png',
    updated: 'June 2026',
    readingTime: '5 min',
    intro: [
      'In Switzerland health insurance is mandatory for everyone, but that does not mean all solutions are equal: premiums, insurance models, deductibles and supplementary cover can vary greatly from one situation to another.',
      'Our job is to help you read your situation — age, family, health, work and budget — before proposing any change.',
      'A coordinated review separates mandatory from optional cover and shows where premiums, deductibles, insurance models and benefits materially affect your choice.',
    ],
    sections: [
      {
        id: 'due-livelli', type: 'text', title: 'Mandatory and supplementary: two distinct levels',
        paragraphs: [
          'Basic insurance (LAMal/KVG) offers identical benefits at every insurer, but differs in premium, insurance model and service. Supplementary insurance is instead unregulated: each company decides benefits, conditions and acceptance.',
          'That is why we always analyse them separately: a good basic premium does not compensate for the wrong supplementary plan, and vice versa.',
        ],
      },
      {
        id: 'da-dove-iniziare', type: 'checklist', title: 'Where to start: the checklist',
        lead: 'The elements we always check before any comparison.',
        items: [
          'Annual deductible and co-payment rate',
          'Insurance model (standard, family doctor, HMO, telemedicine)',
          'Network of contracted doctors and providers',
          'Active supplementary cover and its conditions',
          'Family situation and expected needs over the coming years',
          'Medical treatments already ongoing or planned',
          'Deadlines and cancellation terms of current policies',
        ],
      },
      {
        id: 'percorsi', type: 'paths', title: 'Health area paths',
        links: [
          { href: '/cassa-malati/calcolatore-premi', label: 'LAMal premium calculator 2026–2027', text: 'Compare official net premiums by municipality or postcode for one person or a whole family.' },
          { href: '/cassa-malati/cassa-malati-obbligatoria', label: 'Mandatory health insurance', text: 'What to compare beyond the premium: models, deductibles, service.' },
          { href: '/cassa-malati/cassa-malati-facoltativa', label: 'Supplementary health insurance', text: 'The role of supplementary plans, admission criteria and mistakes to avoid.' },
          { href: '/guide/salute/cambio-cassa-malati-premi-disdetta', label: 'When to switch', text: 'Premiums, timing and cancellation: the practical guide to switching insurers.' },
          { href: '/guide/salute/assicurazione-prenatale', label: 'Prenatal insurance', text: 'What to arrange before your child is born.' },
        ],
      },
      {
        id: 'errori', type: 'errors', title: 'Common mistakes',
        items: [
          'Choosing only on the basis of the lowest monthly premium',
          'Not checking the insurance model and its constraints',
          'Confusing basic and supplementary insurance',
          'Cancelling a supplementary plan before written confirmation of the new one',
          'Not considering the family\u2019s future needs',
        ],
      },
    ],
    faq: [
      { q: 'Is basic cover the same at every insurer?', a: 'Basic insurance benefits are defined by law and are the same everywhere. What changes is the premium, the insurance model, administration and customer service.' },
      { q: 'Can I have basic and supplementary cover with different companies?', a: 'Yes, it is possible and sometimes sensible. It should be assessed case by case, because some insurers link discounts or conditions to combining both covers.' },
      { q: 'When can I switch health insurers?', a: 'For basic cover you can switch at year-end, respecting the cancellation deadlines. Supplementary plans follow their contract terms: often 3 months\u2019 notice. Details in our switching guide.' },
      { q: 'Can a supplementary insurer reject me?', a: 'Yes. Unlike basic insurance, supplementary insurers can require a health questionnaire and apply reservations, exclusions or refusals. That is why you never cancel before written confirmation.' },
    ],
    cta: { title: 'Request a review of your healthcare situation', text: 'Tell us about your current situation: we check model, deductible and supplementary plans and show you the alternatives.', label: 'Request a review' },
    related: ['/guide/salute/cambio-cassa-malati-premi-disdetta', '/guide/salute/assicurazione-prenatale'],
  },
  {
    path: '/cassa-malati/cassa-malati-obbligatoria',
    kind: 'service',
    area: 'Health',
    areaPath: '/cassa-malati',
    eyebrow: 'Health',
    title: 'Mandatory health insurance: what to compare beyond the premium.',
    summary: 'Basic benefits are identical by law. What makes the difference is the insurance model, the deductible and the service.',
    updated: 'June 2026',
    readingTime: '7 min',
    intro: [
      'Many comparisons between health insurers stop at the monthly premium. It is an important criterion, but not enough: two offers with similar premiums can work very differently when you actually need care.',
    ],
    sections: [
      {
        id: 'cosa-valutare', type: 'checklist', title: 'What to assess',
        items: [
          'Monthly premium and its trend over the years',
          'Annual deductible and co-payment rate',
          'Insurance model and constraints on access to care',
          'Accident cover: included or excluded',
          'Administration: app, expense reimbursement, response times',
          'Availability and quality of customer service',
        ],
      },
      {
        id: 'modelli', type: 'cards', title: 'Insurance models',
        lead: 'Each model changes how you access care. There is no best model: there is the one consistent with your habits.',
        cards: [
          { title: 'Standard model', text: 'Free choice of doctor. Higher premium, maximum flexibility. Suited to those who want to decide without constraints.' },
          { title: 'Family doctor', text: 'Your first contact is always your GP, who coordinates visits and specialists. Reduced premium.' },
          { title: 'HMO', text: 'You receive care within a contracted network of doctors. Attractive premiums, but less freedom of choice.' },
          { title: 'Telemedicine', text: 'First contact is by phone or video with a medical centre. Convenient, but requires discipline in following the pathway.' },
        ],
      },
      {
        id: 'franchigie', type: 'cards', title: 'Deductibles and co-payment: what you actually pay',
        lead: 'The deductible is the annual amount you pay before the insurer starts sharing the costs. Once it has been reached, the co-payment also applies.',
        cards: [
          { title: 'Adult deductibles', text: 'The standard deductible is CHF 300. Available options are CHF 500, 1,000, 1,500, 2,000 and 2,500 per calendar year.' },
          { title: 'Child deductibles', text: 'Up to age 18 the standard deductible is CHF 0. CHF 100, 200, 300, 400, 500 or 600 may be selected; insurers do not have to offer every option.' },
          { title: 'The co-payment', text: 'Once the deductible is exceeded, you pay 10% of further LAMal benefits: up to CHF 700 per year for an adult and CHF 350 for a child.' },
        ],
      },
      {
        id: 'scelta-franchigia', type: 'warning', title: 'CHF 300 or CHF 2,500: the comparison that matters',
        text: 'As a practical rule, if you expect LAMal benefits above roughly CHF 1,400 during the year, we start from the CHF 300 deductible. Intermediate deductibles rarely make economic sense: they often provide neither the savings of CHF 2,500 nor the protection of CHF 300. The threshold must still be checked against the actual premium saving offered by your insurer and your ability to absorb an unexpected expense.',
      },
      {
        id: 'attenzione-cambio', type: 'warning', title: 'Mind the switching deadlines',
        text: 'Switching basic insurance is possible at year-end and requires respecting cancellation deadlines. Before signing a new policy, check the dates: you will find the full calendar in our guide to switching health insurers.',
      },
    ],
    faq: [
      { q: 'Can I switch health insurers every year?', a: 'For basic insurance yes, respecting cancellation deadlines (usually by the end of November, effective at year-end). Supplementary plans follow their own contract terms.' },
      { q: 'When does it make sense to exclude accident cover?', a: 'If you work at least 8 hours a week for the same employer, you are already insured against accidents through UVG/LAA and can exclude accident cover from your health insurance, reducing the premium.' },
      { q: 'What changes between the insurance models?', a: 'The first point of contact when you need care: free choice, family doctor, HMO network or telemedicine. With identical statutory benefits, alternative models offer lower premiums in exchange for constraints on the care pathway.' },
      { q: 'Can I have basic and supplementary cover with different companies?', a: 'Yes, it is possible. It should be assessed case by case: sometimes separating is better, sometimes combined conditions are more advantageous.' },
    ],
    cta: { title: 'Compare your current solution', text: 'Send us the details of your current policy: we simulate deductible and model on your real consumption.', label: 'Compare your solution' },
    related: ['/guide/salute/cambio-cassa-malati-premi-disdetta', '/cassa-malati/cassa-malati-facoltativa'],
  },
  {
    path: '/cassa-malati/cassa-malati-facoltativa',
    kind: 'service',
    area: 'Health',
    areaPath: '/cassa-malati',
    eyebrow: 'Health',
    title: 'Supplementary health insurance: the value (and limits) of supplementary plans.',
    summary: 'Supplementary insurance covers what basic insurance does not. But plans are not all equal, and acceptance is not automatic.',
    updated: 'June 2026',
    readingTime: '7 min',
    intro: [
      'Hospital, dental care, complementary medicine, glasses: supplementary plans can make a real, concrete difference. They can also turn out expensive and of little use if chosen without reading the conditions.',
    ],
    sections: [
      {
        id: 'a-cosa-servono', type: 'cards', title: 'What they can be useful for',
        cards: [
          { title: 'Hospital ward', text: 'Private or semi-private room and free choice of doctor in hospital, in Switzerland or abroad.' },
          { title: 'Dental care', text: 'Contributions for treatments and orthodontics, especially for children. Always check annual limits and waiting periods.' },
          { title: 'Complementary medicine', text: 'Reimbursement for non-conventional treatments by recognised therapists.' },
          { title: 'Glasses and lenses', text: 'Periodic contributions for visual aids, with amounts and frequencies varying between insurers.' },
          { title: 'Sport and prevention', text: 'Contributions for gym memberships, courses and preventive check-ups.' },
          { title: 'Abroad and rescue', text: 'Cover for emergency care abroad and transport and rescue costs.' },
        ],
      },
      {
        id: 'non-tutte-uguali', type: 'text', title: 'Not all supplementary plans are equal',
        paragraphs: [
          'Similar names can hide very different benefits: annual maximum amounts, deductibles, waiting periods, lists of recognised therapists. The comparison must be made on the general conditions, not on the product name.',
        ],
      },
      {
        id: 'questionario', type: 'warning', title: 'The health questionnaire',
        text: 'For supplementary plans the insurer can require a questionnaire about your health and apply reservations, exclusions or refusals. Always answer fully and correctly: omissions can lead to refusal of benefits exactly when you need them.',
      },
      {
        id: 'prima-di-cambiare', type: 'checklist', title: 'Before switching',
        items: [
          'Compare the general conditions, not just the premium',
          'Check the annual reimbursement limits for each benefit',
          'Check waiting periods (for example for maternity)',
          'Wait for written confirmation from the new insurer',
          'Only then: cancel the previous cover and keep proof of sending',
        ],
      },
    ],
    faq: [
      { q: 'Can a supplementary insurer reject me?', a: 'Yes. Supplementary insurers are free to accept, apply reservations or refuse. That is why the timing of a switch is delicate and must be planned.' },
      { q: 'Can I switch only the basic insurance and keep the supplementary plan?', a: 'Yes, it is generally possible. Watch out for any discounts linked to the combination and for the conditions of the supplementary contract.' },
      { q: 'What happens if I forget a condition in the questionnaire?', a: 'The insurer can contest the contract and refuse the related benefits. Better to declare everything and accept a possible reservation than risk a refusal when you need it.' },
      { q: 'Can I insure a child before birth?', a: 'Yes, with prenatal insurance many insurers accept the child without a health questionnaire. It is one of the few cases where access to supplementary plans is simplified: we cover it in the dedicated guide.' },
    ],
    cta: { title: 'Review your supplementary cover', text: 'We analyse the conditions of your supplementary plans and compare them with market alternatives.', label: 'Review supplementary plans' },
    related: ['/guide/salute/assicurazione-prenatale', '/guide/salute/cambio-cassa-malati-premi-disdetta'],
  },
  {
    path: '/guide/salute/cambio-cassa-malati-premi-disdetta',
    kind: 'guide',
    area: 'Health',
    areaPath: '/cassa-malati',
    eyebrow: 'Practical guide · Health',
    title: 'Switching health insurers: premiums, timing and cancellation.',
    summary: 'The switching calendar, the documents to check and the mistakes to avoid when the new premiums arrive.',
    updated: 'June 2026',
    readingTime: '8 min',
    intro: [
      'Every autumn insurers communicate the following year\u2019s premiums. It is the right time to compare, but also when most mistakes are made: cancellations sent late, models chosen in haste, supplementary plans taken for granted.',
      'This guide walks you through in order: first the comparison, then the decision, finally the correct cancellation.',
    ],
    sections: [
      {
        id: 'prima-di-cambiare', type: 'text', title: 'Before switching',
        paragraphs: ['Before even looking at premiums, get your current situation in order.'],
        bullets: [
          'Compare premium and insurance model, not just the premium',
          'Check the deductible and co-payment of your current policy',
          'Distinguish between basic and supplementary insurance: different rules',
          'Check for any pending administrative situations or premium arrears',
        ],
      },
      {
        id: 'calendario', type: 'timeline', title: 'The switching calendar',
        lead: 'An indicative sequence: actual deadlines should always be checked each year, as they can vary.',
        steps: [
          { label: 'October', title: 'New premiums arrive', text: 'Insurers communicate the following year\u2019s premiums. Time to gather data and compare.' },
          { label: 'November', title: 'The comparison', text: 'Simulate deductible and model on your real consumption. If you switch, request confirmation from the new insurer.' },
          { label: 'End of November', title: 'The cancellation deadline', text: 'Cancellation of basic cover must reach the old insurer by the deadline, in writing and with proof of sending.' },
          { label: '1 January', title: 'The new cover', text: 'The new basic insurance takes effect on the first of January. Keep the confirmation and documents.' },
        ],
      },
      {
        id: 'disdetta', type: 'text', title: 'How to send the cancellation',
        paragraphs: ['The cancellation must be clear, complete and traceable.'],
        bullets: [
          'Written form, preferably registered letter',
          'Name, date of birth and policy or insured person number',
          'Requested termination date (usually 31 December)',
          'Signature and date',
          'Always keep proof of sending and receipt',
        ],
      },
      {
        id: 'base-complementare', type: 'warning', title: 'Basic and supplementary: different rules',
        text: 'The switching rules for basic insurance and supplementary plans can be very different: notice periods, minimum duration, health questionnaire. Never cancel supplementary cover before written acceptance of the new solution.',
      },
      {
        id: 'checklist', type: 'checklist', title: 'The switching checklist',
        items: [
          'I compared the insurance model, not just the premium',
          'I checked the deductible best suited to my consumption',
          'I checked accident cover (included or excluded)',
          'I received written confirmation of the new solution',
          'I sent the cancellation by the deadline',
          'I kept proof of sending',
        ],
      },
    ],
    faq: [
      { q: 'By when must I cancel my health insurance?', a: 'For basic insurance, cancellation must usually reach the insurer by the end of November, effective 31 December. Always check the exact deadline communicated with the new premiums.' },
      { q: 'Is cancellation by email valid?', a: 'It depends on the insurer: some accept digital forms, others require a letter. The safest method remains registered mail with proof of receipt.' },
      { q: 'Can I switch supplementary plans at the same time?', a: 'Supplementary plans have their own terms, often different from basic insurance. Before cancelling you need written confirmation of acceptance from the new insurer.' },
      { q: 'What happens if I have premium arrears?', a: 'Pending administrative situations can complicate the switch. Better to clarify them first, possibly with the help of an advisor.' },
    ],
    cta: { title: 'Have your cancellation checked', text: 'Before sending your cancellation, let us check it together: deadlines, form and correct content.', label: 'Have your cancellation checked' },
    related: ['/cassa-malati/cassa-malati-obbligatoria', '/cassa-malati/cassa-malati-facoltativa'],
  },
  {
    path: '/guide/salute/assicurazione-prenatale',
    kind: 'guide',
    area: 'Health',
    areaPath: '/cassa-malati',
    eyebrow: 'Practical guide · Health',
    title: 'Prenatal insurance: what to arrange before the birth.',
    summary: 'Some cover for your baby is simpler to activate before the birth. Here is what to assess and when.',
    updated: 'June 2026',
    readingTime: '6 min',
    intro: [
      'With a child on the way, formalities pile up. Some insurance decisions, however, are best made during pregnancy: prenatal registration often allows the baby to be accepted without a health questionnaire.',
    ],
    sections: [
      {
        id: 'perche-prima', type: 'text', title: 'Why think about it early',
        paragraphs: [
          'Many insurers allow you to register the baby before birth, accepting them without reservations, even for supplementary plans. After the birth, a health questionnaire may be required instead.',
          'Also, in the first months after the birth you will already have plenty to manage: making insurance decisions early removes one worry.',
        ],
      },
      {
        id: 'cosa-valutare', type: 'checklist', title: 'What to assess',
        items: [
          'Registration of the baby for basic insurance',
          'Supplementary plans: hospital, dental care, complementary medicine',
          'Preferred hospital ward for the birth and for the baby',
          'Financial protection for the family (liability, household)',
          'Pension planning and income protection for the parents',
        ],
      },
      {
        id: 'timeline', type: 'timeline', title: 'The recommended timeline',
        steps: [
          { label: 'During pregnancy', text: 'Get informed about insurers, compare supplementary plans and assess prenatal registration.' },
          { label: 'Shortly before the birth', text: 'Confirm your choices, prepare documents and parents\u2019 details.' },
          { label: 'Right after the birth', text: 'Notify the insurer of the birth and complete basic registration within the required deadlines.' },
          { label: 'First months', text: 'Review the family\u2019s overall cover: liability, household, parents\u2019 pension planning.' },
        ],
      },
      {
        id: 'documenti', type: 'text', title: 'Useful documents and information',
        bullets: [
          'Parents\u2019 personal details and AHV/AVS numbers',
          'Expected due date',
          'Domicile and chosen health insurer',
          'List of desired supplementary plans',
          'Any particular needs known during pregnancy',
        ],
      },
    ],
    faq: [
      { q: 'When should I start getting informed?', a: 'Ideally in the second trimester of pregnancy: you have time to compare calmly and complete prenatal registration.' },
      { q: 'Does cover start from birth?', a: 'With prenatal registration, cover can run directly from birth, without interruption. Exact conditions depend on the insurer.' },
      { q: 'Which cover requires advance assessment?', a: 'After birth, supplementary plans may require a health questionnaire for the baby. Before birth, many insurers accept without reservations instead.' },
      { q: 'Can I change my choice after the birth?', a: 'Basic insurance can be changed under the ordinary terms. Supplementary plans should be assessed more cautiously, as new access may require the questionnaire.' },
    ],
    cta: { title: 'Prepare your baby\u2019s cover', text: 'We help you compare prenatal options and organise everything before the birth.', label: 'Prepare the cover' },
    related: ['/cassa-malati/cassa-malati-facoltativa', '/guide/privati/trasferirsi-in-svizzera'],
  },
];
